import { ExclamationCircleOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as Scroll from 'react-scroll';
import { toast } from 'react-toastify';

import {
  AutopaySettings,
  CompanyFragment,
  InitiateNachaPaymentDocument,
  InitiateNachaPaymentMutationVariables,
  InitiatePaymentDocument,
  InitiatePaymentMutationVariables,
  InitiateRepayPaymentDocument,
  InitiateRepayPaymentMutationVariables,
  PortalBorrowerAchAccountFragment,
  PortalChargeFragment,
  PortalLoan,
  PortalPaymentDueFragment,
  PortalWorkoutPlanFragment,
  WorkoutPlanType,
} from '@willow/graphql-iso/src/portal';
import { add, calculateAdditionalEscrowShortage } from '@willow/shared-iso';
import {
  ControlledCurrencyInput,
  ControlledSelect,
  isQuietValidationError,
  Loader,
  paymentAmountFormat,
} from '@willow/shared-web';
import { OverlayTrigger, Tooltip } from '@willow/shared-web/bootstrap';
import { EscrowPaymentHistoryItemId, LoanId, LoanStatus } from '@willow/types-iso';

import { DwollaLimitModal } from '../dwolla-limit-modal/DwollaLimitModal';
import { DwollaPaymentMethod } from '../payment-method/DwollaPaymentMethod';
import { NachaPaymentMethod } from '../payment-method/NachaPaymentMethod';
import { RepayPaymentMethod } from '../payment-method/RepayPaymentMethod';
import { AutopaySettingForm } from './autopay-setting/AutopaySettingForm';
import {
  getPaymentAllocations,
  getPaymentTypeDescription,
  getPaymentTypeOptions,
  PaymentBank,
  PaymentFormSchema,
  TPaymentFormSchema,
} from './helpers';
import { PaymentConfirmModal } from './payment-confirm-modal/PaymentConfirmModal';
import { getPaymentFormAmountDueDisplay, paymentFormAmountDuesForDisplay } from './util';

import './PaymentForm.scss';

const SCROLLER = Scroll.scroller;
export interface PaymentFormProps {
  loanId: LoanId;
  loanStatus: LoanStatus;
  company: CompanyFragment;
  totalAmountDue: number;
  totalUpcomingAmountDue: number;
  totalAmountDueToReinstateLoan: number;
  paymentsDue: PortalPaymentDueFragment[];
  chargesDue: PortalChargeFragment[];
  nachaEnabled: boolean;
  achAccounts: PortalBorrowerAchAccountFragment[];
  repayEnabled: boolean;
  repayAccounts: PortalLoan['repayAccounts'];
  autopaySettings?: AutopaySettings;
  onPaymentSuccess: () => void;
  refetch: () => void;
  exactPaymentOnly?: boolean;
  shortageAmount: number;
  spreadMonths: number;
  escrowShortageAnalysisId: EscrowPaymentHistoryItemId | undefined;
  activeWorkoutPlan: PortalWorkoutPlanFragment | undefined;
  escrowBalance: number;
  isPastDue?: boolean;
  totalStatementAmountDue: number;
  isForbearanceOrFlexModificationWorkoutPlan: boolean;
}

export const PaymentForm = ({
  loanId,
  loanStatus,
  company,
  totalAmountDue,
  totalUpcomingAmountDue,
  totalAmountDueToReinstateLoan,
  paymentsDue,
  chargesDue,
  nachaEnabled,
  achAccounts,
  repayEnabled,
  repayAccounts,
  autopaySettings,
  onPaymentSuccess,
  refetch,
  exactPaymentOnly = false,
  shortageAmount,
  spreadMonths,
  escrowShortageAnalysisId,
  activeWorkoutPlan,
  escrowBalance,
  isPastDue,
  totalStatementAmountDue,
  isForbearanceOrFlexModificationWorkoutPlan,
}: PaymentFormProps) => {
  const [initiatePayment] = useMutation(InitiatePaymentDocument);
  const [initiateRepayPayment] = useMutation(InitiateRepayPaymentDocument);
  const [initiateNachaPayment] = useMutation(InitiateNachaPaymentDocument);

  const [activeModal, setActiveModal] = useState<'confirm' | 'dwollaLimit' | undefined>(undefined);
  const [showPaymentForm, setShowPaymentForm] = useState(!autopaySettings);

  // Use totalAmountDue if there's an actual payment due, otherwise use upcoming amount
  // BUT... if we're on a forbearance or flex modification workout plan, don't show the upcoming amount due
  const fullPaymentAmount =
    totalAmountDue > 0 ? totalAmountDue : !isForbearanceOrFlexModificationWorkoutPlan ? totalUpcomingAmountDue : 0;

  const totalLateFeesDue = add(
    ...chargesDue
      .filter((charge) => charge.__typename === 'Fee' && charge.lineItems.some((item) => item.type === 'LATE'))
      .map((charge) => charge.amountRemaining),
  );
  const defaultPaymentBank = nachaEnabled ? achAccounts.at(0) : repayEnabled ? repayAccounts.at(0) : undefined;

  // Don't display amount due breakdowns when paying on certain workout plans
  const shouldHideAmountDuesDisplay =
    activeWorkoutPlan &&
    [WorkoutPlanType.Forbearance, WorkoutPlanType.FlexModification, WorkoutPlanType.RepaymentPlan].includes(
      activeWorkoutPlan.type,
    );

  const additionalEscrowShortage = calculateAdditionalEscrowShortage(shortageAmount, spreadMonths);
  const fullPaymentWithShortageAmount = add(fullPaymentAmount, additionalEscrowShortage);

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<TPaymentFormSchema>({
    resolver: zodResolver(PaymentFormSchema),
    defaultValues: {
      paymentType: 'fullPayment',
      amount: 0,
      fullPaymentAmount,
      fullPaymentWithShortageAmount,
      paymentBank: defaultPaymentBank,
    },
  });

  useEffect(() => {
    // This reset the form next fullPaymentAmount once the form is submitted
    setValue('amount', 0);
    setValue('paymentType', 'fullPayment');
  }, [fullPaymentAmount, setValue]);

  const paymentType = useWatch({ control, name: 'paymentType' });
  const amount = useWatch({ control, name: 'amount' });
  const paymentBank = useWatch({ control, name: 'paymentBank' });

  // Get unfulfilled payment dues for display (includes fees and advances)
  // Use actual due payments when there's a payment amount due (excluding charges), otherwise use upcoming payments
  // Don't display amount due breakdowns when paying on certain workout plans
  const amountDueItems = getPaymentFormAmountDueDisplay(paymentsDue, chargesDue, {
    onlyDue: totalAmountDue > 0,
  });
  const shortagePaidUpfront = paymentType === 'fullPaymentWithShortage';
  const baseAmountDueDisplayItems = paymentFormAmountDuesForDisplay(amountDueItems, shortagePaidUpfront, spreadMonths);

  // Add escrow shortage to display items when paying with shortage upfront
  const shortageDisplayItem = {
    desktop: `${paymentAmountFormat(shortageAmount)} (Escrow shortage)`,
  };
  const amountDueDisplayItems =
    paymentType === 'fullPaymentWithShortage' && shortageAmount > 0
      ? [...baseAmountDueDisplayItems, shortageDisplayItem]
      : baseAmountDueDisplayItems;

  const onBankUpdate = useCallback(
    (bank: PaymentBank | undefined) => {
      setValue('paymentBank', bank);

      if (bank?.__typename === 'RepayAccount') {
        refetch();
      }
    },
    [refetch, setValue],
  );

  const accountName =
    paymentBank?.__typename === 'DwollaBank'
      ? paymentBank.name
      : paymentBank?.__typename === 'RepayAccount'
      ? paymentBank.accountName
      : paymentBank?.__typename === 'BorrowerAchAccount'
      ? paymentBank.accountName
      : 'selected bank';

  const onFormSubmit = () => {
    setActiveModal('confirm');
  };

  const submitPayment = async () => {
    if (!paymentBank) {
      throw new Error('Bank account is not properly set.');
    }

    try {
      // Determine additional allocations based on payment type
      const allocations = getPaymentAllocations({
        paymentType,
        amount,
        escrowShortageAnalysisId,
        additionalEscrowShortage,
        totalAmountDue,
        totalUpcomingAmountDue,
        totalAmountDueToReinstateLoan,
        fullPaymentAmount,
        fullPaymentWithShortageAmount,
        activeWorkoutPlan,
      });

      if (paymentBank.__typename === 'DwollaBank') {
        const input: InitiatePaymentMutationVariables = {
          borrowerBankUrl: paymentBank.links.self,
          loanId,
          companyId: company.id,
          amount: allocations.paymentAmount,
          ...allocations,
        };

        await initiatePayment({ variables: input });
      } else if (paymentBank.__typename === 'RepayAccount') {
        const input: InitiateRepayPaymentMutationVariables = {
          repayAccountId: paymentBank.id,
          companyId: company.id,
          loanId,
          amount: allocations.paymentAmount,
          ...allocations,
        };

        await initiateRepayPayment({ variables: input });
      } else if (paymentBank.__typename === 'BorrowerAchAccount') {
        const input: InitiateNachaPaymentMutationVariables = {
          companyId: company.id,
          loanId,
          borrowerAchAccountId: paymentBank.id,
          amount: allocations.paymentAmount,
          additionalPrincipal: allocations.additionalPrincipal,
          additionalEscrow: allocations.additionalEscrow,
          additionalSuspense: allocations.additionalSuspense,
          escrowShortageAnalysisId: allocations.escrowShortageAnalysisId,
        };

        await initiateNachaPayment({ variables: input });
      }

      reset();
      refetch();
      onPaymentSuccess();
      setActiveModal(undefined);

      // Scroll to payment header so user sees the update
      SCROLLER.scrollTo('payment-header', { offset: -150 });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '';
      // Check for Dwolla transaction limit error
      if (errorMessage.includes('Invalid amount. The supplied amount is greater than your transaction limit.')) {
        setActiveModal('dwollaLimit');
      } else {
        toast.error(
          isQuietValidationError(err)
            ? errorMessage
            : errorMessage || 'Something went wrong with submitting your payment.',
        );
      }
    }
  };

  const paymentTypeOptions = getPaymentTypeOptions({
    fullPaymentAmount,
    totalStatementAmountDue,
    totalLateFeesDue,
    exactPaymentOnly,
    additionalEscrowShortage,
    escrowShortageAnalysisId,
    escrowBalance,
    loanStatus,
  });

  const { disabledReason, submitDisabled, extraInfo } =
    paymentTypeOptions.find((option) => option.value === paymentType) || {};
  const paymentTypeDescription = getPaymentTypeDescription(paymentType);
  const paymentAmount =
    paymentType === 'fullPayment'
      ? fullPaymentAmount
      : paymentType === 'fullPaymentWithShortage'
      ? fullPaymentWithShortageAmount
      : amount;

  return (
    <>
      <section
        className={classnames(['payment-form', 'make-payment__container', 'mt-3'], {
          'payment-form--busy': isSubmitting,
        })}
      >
        <h1>Make a Payment</h1>

        <form onSubmit={handleSubmit(onFormSubmit)} data-testid="payment-form">
          <h2>
            Payment Method
            <OverlayTrigger
              placement="top"
              trigger={['hover', 'focus']}
              overlay={<Tooltip>Learn more about online payments at the FAQ page</Tooltip>}
            >
              <QuestionCircleOutlined />
            </OverlayTrigger>
          </h2>

          {nachaEnabled ? (
            <NachaPaymentMethod
              companyId={company.id}
              loanId={loanId}
              achAccounts={achAccounts}
              onBankUpdate={onBankUpdate}
            />
          ) : repayEnabled ? (
            <RepayPaymentMethod
              companyId={company.id}
              loanId={loanId}
              repayAccounts={repayAccounts}
              onBankUpdate={onBankUpdate}
            />
          ) : (
            <div className="payment-form__dwolla">
              <DwollaPaymentMethod
                companyName={company.name}
                autopaySettings={autopaySettings}
                onBankUpdate={onBankUpdate}
              />
            </div>
          )}

          {errors.paymentBank && <div className="u-fs-2 u-color-red1">{errors.paymentBank.message}</div>}

          <AutopaySettingForm
            autopaySettings={autopaySettings}
            paymentBank={
              paymentBank?.__typename === 'DwollaBank' || paymentBank?.__typename === 'RepayAccount'
                ? paymentBank
                : undefined
            }
            loanStatus={loanStatus}
            loanId={loanId}
            totalAmountDue={totalAmountDue}
            isPastDue={isPastDue}
            repayAccounts={repayAccounts}
            repayEnabled={repayEnabled}
          />

          {showPaymentForm ? (
            <>
              <h2 data-testid="payment-form-amount-title">Payment Details</h2>

              <div className="u-border-color-bark1 u-border-1 u-border-r-3 p-3">
                <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                  <div className="payment-form__field payment-form__field-type">
                    <label htmlFor="paymentType">Payment Type</label>
                    <ControlledSelect
                      fieldName="paymentType"
                      control={control}
                      isClearable={false}
                      options={paymentTypeOptions}
                    />
                  </div>

                  <div className="payment-form__field payment-form__field-amount">
                    <label htmlFor="amount">Amount</label>
                    <div className="payment-form__amount-input">
                      {paymentType === 'fullPayment' || paymentType === 'fullPaymentWithShortage' ? (
                        // Show full payment amount or full payment with shortage amount as a disabled input
                        <input
                          type="text"
                          value={paymentAmountFormat(
                            paymentType === 'fullPayment' ? fullPaymentAmount : fullPaymentWithShortageAmount,
                          )}
                          disabled
                        />
                      ) : (
                        <ControlledCurrencyInput
                          control={control}
                          fieldName="amount"
                          data-testid="payment-form-amount-input"
                          disabled={submitDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {disabledReason && (
                  <div className="mt-2 p-1 u-border-color-red1 u-border-1">
                    <span className="u-fs-2 d-flex align-items-center gap-2">
                      <ExclamationCircleOutlined className="u-color-red1" /> {disabledReason}
                    </span>
                  </div>
                )}

                {extraInfo && (
                  <div className="mt-2 p-1 u-border-color-blue1 u-border-1">
                    <span className="u-fs-2 d-flex align-items-center gap-2">
                      <InfoCircleOutlined className="u-color-blue1" /> {extraInfo}
                    </span>
                  </div>
                )}

                {paymentTypeDescription && (
                  <div>
                    <span className="u-fs-2 u-color-bark3">{paymentTypeDescription}</span>
                  </div>
                )}

                {/* Payment details blurb for Full Payment types */}
                {(paymentType === 'fullPayment' || paymentType === 'fullPaymentWithShortage') &&
                  amountDueDisplayItems.length > 0 &&
                  !shouldHideAmountDuesDisplay && (
                    <div className="mt-2 p-1">
                      <span className="u-fs-2 fw-bold">Amount due includes:</span>
                      {amountDueDisplayItems.map((item, index) => (
                        <div key={index}>
                          <span className="u-fs-3">{item.desktop}</span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <div className="payment-form__submit">
                <div className="payment-form__submit_inner">
                  <input
                    type="submit"
                    value="Submit Payment"
                    data-ghost="portal-loan--form-submit-payment-button"
                    disabled={isSubmitting || submitDisabled}
                    data-testid="payment-form-submit"
                  />
                  {isSubmitting && <Loader />}
                </div>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="payment-details__addition__btn mt-4 u-fs-4 mb-4"
              onClick={() => setShowPaymentForm(true)}
            >
              Make a one-time payment
            </button>
          )}
        </form>
      </section>

      <PaymentConfirmModal
        showModal={activeModal === 'confirm'}
        onClose={() => setActiveModal(undefined)}
        paymentAmount={paymentAmount}
        paymentType={paymentType}
        bankName={accountName}
        autopayEnabled={!!autopaySettings}
        amountDue={fullPaymentAmount}
        onConfirm={submitPayment}
      />

      <DwollaLimitModal
        company={company}
        showModal={activeModal === 'dwollaLimit'}
        onClose={() => setActiveModal(undefined)}
      />
    </>
  );
};
